import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { breakpoints } from 'styles';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  NoEmailAddress,
  Pagination,
  Search,
  SortBy,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  GroupUsersAdd,
  GroupUsersInvite,
  GroupUsersInviteSingle,
  GroupUsersRemove,
  GroupUsersRemoveAll
} from '.';

import type { EmailTemplateData } from 'shared-types';
import type { GetGroupUsersRes } from 'shared-types/responses';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
}

const GroupUsers = ({ emailTemplates, emailTemplatesDefault }: Props) => {
  const {
    messages,
    pageState: { group },
    serverConfig
  } = useReduxState();

  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const [sortBy, setSortBy] = useState(
    serverConfig.userUseName ? 'name' : 'username'
  );

  const { data, loading, updateData } = useApiData<GetGroupUsersRes>({
    url:
      `/admin/groups/${group.id}/users?page=${page}` +
      (searchString ? `&search=${searchString}` : '') +
      (sortBy ? `&sortBy=${sortBy}` : '')
  });

  const listOfUserIds = data?.listOfUserIds || [];
  const pages = data?.pages || 0;
  const users = data?.users || [];

  const handleSearch = ({ page, search }: { page: number; search: string }) => {
    setPage(page);
    setSearchString(search);
  };

  const handleSortBy = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy);
  };

  return (
    <div className="users">
      <Box
        loading={loading}
        padding={0}
        title={messages.commonUsers}
        headerContent={
          <>
            <div style={{ display: 'inline-block', marginRight: 10 }}>
              <GroupUsersAdd
                emailTemplates={emailTemplates}
                emailTemplatesDefault={emailTemplatesDefault}
                updateUsers={updateData}
                userIds={listOfUserIds}
              />
            </div>
            <div style={{ display: 'inline-block' }}>
              <GroupUsersInvite
                emailTemplates={emailTemplates}
                emailTemplatesDefault={emailTemplatesDefault}
                updateUsers={updateData}
                users={users}
              />
            </div>
          </>
        }
        subHeaderContent={
          <div className="subheader">
            <div className="search">
              <Search
                onSearch={handleSearch}
                placeholder={messages.settingsSearchForUser}
                size="header"
              />
            </div>
            <div className="sort">
              <SortBy
                onChange={handleSortBy}
                options={[
                  serverConfig.userUseName
                    ? {
                        text: messages.commonName,
                        value: 'name'
                      }
                    : {
                        text: messages.commonUsername,
                        value: 'username'
                      },
                  {
                    text: messages.commonAdded,
                    value: 'added'
                  },
                  {
                    text: messages.commonInviteEmail,
                    value: 'inviteEmailDate'
                  },
                  {
                    text: messages.usersLastLogin,
                    value: 'lastLogin'
                  }
                ]}
                value={sortBy}
              />
            </div>
            <div className="more">
              <MoreOptions category="lighter">
                <GroupUsersRemoveAll updateUsers={updateData} />
              </MoreOptions>
            </div>
          </div>
        }
      >
        {users && users.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              <BoxTableCell>{messages.commonAdded}</BoxTableCell>
              <BoxTableCell>{messages.commonInviteEmail}</BoxTableCell>
              <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>
            {users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={user} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon="check-circle"
                  label={messages.commonAdded}
                >
                  <DisplayFormattedDate date={user.addedDate} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  label={messages.commonInviteEmail}
                  {...(user.inviteEmailDate ? { icon: 'envelope' } : {})}
                >
                  {user.inviteEmailDate ? (
                    <DisplayFormattedDate date={user.inviteEmailDate} />
                  ) : user.email ? null : (
                    <NoEmailAddress />
                  )}
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  icon="key"
                  iconStyle={{
                    fontSize: '1.1rem',
                    position: 'relative',
                    top: -1
                  }}
                  label={messages.usersLastLogin}
                >
                  {user.lastLogin ? (
                    <DisplayFormattedDate date={user.lastLogin} />
                  ) : null}
                </BoxTableCell>
                <BoxTableCell
                  shrink={true}
                  verticalBreakpoint={breakpoints.medium}
                >
                  <MoreOptions>
                    {user.email ? (
                      <GroupUsersInviteSingle
                        emailTemplates={emailTemplates}
                        emailTemplatesDefault={emailTemplatesDefault}
                        targetUser={user}
                        updateUsers={updateData}
                      />
                    ) : null}
                    <GroupUsersRemove
                      updateUsers={updateData}
                      userId={user.id}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={pages}
        onPageChange={({ page }) => setPage(page)}
      />

      <style jsx>{`
        .subheader {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          width: 100%;
        }
        .search {
          flex-grow: 1;
        }
        .more,
        .sort {
          margin-left: 10px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .search :global(input) {
            min-width: 260px;
          }
        }
      `}</style>
    </div>
  );
};

export default GroupUsers;
